<template>
  <v-card class="h-100">
    <v-chart ref="chart" :options="options" :init="chartInit" @contextmenu="contextmenu" />
  </v-card>
</template>

<script>
import { DEFAULT_COLORS, WORKPRCS_COLORS, WORKPRCS_COLORS_ABBR } from '@/constants';
import chartable from '@/mixins/chartable';

export default {
  mixins: [chartable],
  props: {
    system: {
      type: Object,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    values: {
      type: Array,
      required: true,
    },
    colors: {
      type: Array,
      default() {
        return DEFAULT_COLORS;
      },
    },
    grid: {
      type: Object,
      default() {
        return {
          top: '10px',
          bottom: '20px',
          left: '50px',
          right: '10px',
        };
      },
    },
    contextmenu: {
      type: Function,
      default: e => e,
    },
  },
  data: () => ({
    dataColorCoord: WORKPRCS_COLORS,
    dataColorCoordAbbr: WORKPRCS_COLORS_ABBR,
    options: {},
    series: [],
    systemChanged: true,
    loading: false,
  }),
  computed: {
    echart() {
      return this.$refs.chart.$refs.chart.chart;
    },
    chartInit() {
      return {
        grid: this.grid,
        xAxis: {
          type: 'value',
          axisLabel: {
            fontWeight: 'normal',
            fontSize: 9,
            color: '#bbbbaa',
          },
        },
        yAxis: {
          type: 'category',
          data: [''],
          axisLabel: {
            fontWeight: 'normal',
            fontSize: 9,
            color: '#bbbbaa',
          },
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: params => this.tooltipFormatter(params),
        },
      };
    },
  },
  watch: {
    system: {
      immediate: true,
      handler: 'changSystemHandler',
    },
    items: {
      deep: true,
      handler: 'changItemsHandler',
    },
    isDark: {
      handler: 'changColorHandler',
    },
  },
  methods: {
    changSystemHandler() {
      this.systemChanged = true;
      this.loading = true;
    },
    changItemsHandler() {
      if (!this.systemChanged) this.changColorHandler();
    },
    changeValuesHandler() {
      if (this.systemChanged) {
        this.options = this.getOptions();
        this.loading = false;
      } else {
        this.options = {
          animation: true,
          series: this.getSeries(),
        };
      }
      this.systemChanged = false;
    },
    changColorHandler() {
      const statuses = this.dataColorCoord[0].status;
      this.options.series.forEach(series => {
        let names = series.name.split('_');
        let statusIdx = statuses.findIndex(s => s == parseInt(names[1]));
        series.data.forEach((data, dataIdx) => {
          data.label.color = this.getReverseThemeColor(this.chartColor.splitArea, 10, 60);
          data.itemStyle.color = this.getThemeColor(this.dataColorCoord[parseInt(names[0])].colors[statusIdx], 10, 10);
          data.itemStyle.opacity = this.items[dataIdx].active ? 0.8 : 0.2;
        });
      });
    },
    getOptions() {
      this.series = []; // options를 다시 만드는 상황에서는 this.series를 초기화해준다.
      return {
        yAxis: {
          data: this.items.map(item => item.shortAlias),
        },
        series: this.getSeries(),
      };
    },
    getSeries() {
      const statuses = this.dataColorCoord[0].status;
      for (let idx = 1; idx < this.dataColorCoord.length; idx++) {
        const typeRow = this.dataColorCoord[idx];
        typeRow.colors.forEach((color, statusIdx) => {
          let seriesOne = this.series.find(s => s.name == idx + '_' + statuses[statusIdx]);
          if (seriesOne === undefined) {
            seriesOne = {
              type: 'bar',
              name: idx + '_' + statuses[statusIdx],
              stack: 'stack',
              data: this.items.map(it => ({
                value: 0,
                label: {
                  show: true,
                  position: 'inside',
                  color: this.getReverseThemeColor(this.chartColor.splitArea, 10, 60),
                  formatter: param => (param.value ? `${param.value}` : ''),
                },
                itemStyle: {
                  color: this.getThemeColor(color, 10, 10),
                  opacity: it.active ? 0.8 : 0.2,
                },
              })),
            };
            this.series.push(seriesOne);
          }
          seriesOne.data.forEach((sd, sdIdx) => {
            const instData = this.values.find(val => val.id == this.items[sdIdx].id);
            // 각 인스턴스의 데이터에서 type값과  status 값이
            // 지금 현재 dataColorCoord 루프의 type/status값과 일치되는 것을 찾아서
            // 현재 series의 data.value에 건수를 할당해준다.
            const typeStatusDatum = instData.data.find(datum => datum[0] == idx && datum[1] == statuses[statusIdx]);
            if (typeStatusDatum === undefined) {
              sd.value = 0;
            } else {
              sd.value = typeStatusDatum[2];
            }
          });
        });
      }
      return this.series;
    },
    tooltipFormatter(params) {
      // console.log(params);
      let total = 0;
      const item = this.items.find(item => item.shortAlias == params[0].axisValue);
      if (item === undefined) return '';
      this.$emit('update:selectedDescendant', item);
      const label = [this.circle(item.color), `<span style="color:${item.color};font-weight:900;">`, item.shortAlias, '</span> : '];
      let prevType = '';
      let closeColorHtml = '</span> : ';
      const list = params.reduce((acc, param) => {
        let cnt = parseInt(param.value);
        if (cnt > 0) {
          let colorHtml = `<span style="color:${param.color};font-weight:900;margin:0 0 0 10px;">`;
          total += cnt;
          let names = param.seriesName.split('_');
          let typeDesc = this.$t(`workprcs.type.${names[0]}`);
          let statusDesc = this.$t(`workprcs.status.${names[1]}`);
          if (prevType != names[0]) {
            // 처음이거나 새로운 type으로 바뀌었을 때
            let listItem = [];
            if (prevType != '') {
              listItem.push('<br/>');
            }
            listItem.push(colorHtml, typeDesc + '-' + statusDesc, closeColorHtml, param.value);
            acc.push(listItem.join(''));
            prevType = names[0];
          } else {
            acc.push([colorHtml, statusDesc, closeColorHtml, param.value].join(''));
          }
        }
        return acc;
      }, []);
      label.push(total, '<br/>');
      list.unshift(label.join(''));
      return list.join('');
    },
  },
};
</script>
